import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { useDashboardStats, type DashboardStats } from "@/lib/queries/dashboard"

/**
 * Dashboard — landing page for signed-in admins.
 *
 * Hits GET /admin/dashboard/stats on mount, polls every 60s, and refetches
 * on window focus so the four KPI cards stay current without an explicit
 * refresh action.
 *
 * Loading shows em-dashes (preserves the layout). Error falls back to "—"
 * with a small caption so the rest of the page stays usable; the next
 * poll will retry automatically.
 */
export default function Dashboard() {
  const q = useDashboardStats()

  const cards: { label: string; key: keyof DashboardStats; hint: string }[] = [
    { label: "Total users", key: "users", hint: "All registered accounts." },
    {
      label: "Videos uploaded",
      key: "videos",
      hint: "Non-deleted videos in the catalog.",
    },
    {
      label: "Active subscriptions",
      key: "activeSubscriptions",
      hint: "Users with a current paid subscription.",
    },
    {
      label: "Pending reports",
      key: "pendingReports",
      hint: "User-submitted reports awaiting admin action.",
    },
  ]

  const stats = q.data
  const showError = q.isError && !stats

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">
          Welcome back
        </h2>
        <p className="text-muted-foreground">
          Manage Good Talk content, people, and settings from one place.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => {
          const value = stats ? formatNumber(stats[c.key]) : "—"
          return (
            <Card key={c.label}>
              <CardHeader className="pb-2">
                <CardDescription>{c.label}</CardDescription>
                <CardTitle className="text-3xl tabular-nums">
                  {value}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-muted-foreground">
                {showError
                  ? "Couldn't load — retrying."
                  : q.isLoading
                    ? "Loading…"
                    : c.hint}
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Locale-aware thousands separator, no decimals. 1234567 → "1,234,567".
 * Falls back to the raw number on environments without Intl support.
 */
function formatNumber(n: number): string {
  try {
    return new Intl.NumberFormat().format(n)
  } catch {
    return String(n)
  }
}
